import React, { useState, useEffect } from 'react';
import { ArrowRight, CheckCircle, Phone } from 'lucide-react';
import AnimateOnScroll from './AnimateOnScroll';
import SectionHeader from './SectionHeader';
import { VEHICLE_CATEGORIES } from '../constants/categories';
import { subscribeToVehicles } from '../services/vehicleService';

const PLACEHOLDER_IMAGE =
  'https://images.unsplash.com/photo-1492144534245-9b9fc0e4d8e2?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80';

const Inventory = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const unsubscribe = subscribeToVehicles(
      (data, err) => {
        if (err) {
          setError('Unable to load inventory. Please try again later.');
        } else {
          setVehicles(data);
          setError(null);
        }
        setLoading(false);
      },
      { availableOnly: true }
    );

    return unsubscribe;
  }, []);

  const filteredVehicles =
    selectedCategory === 'all'
      ? vehicles
      : vehicles.filter((v) => v.category === selectedCategory);

  return (
    <section id="inventory" className="relative py-28 section-mesh overflow-hidden">
      <div className="absolute inset-0 section-grid-bg opacity-30 pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeader
          badge="Our Collection"
          title="Luxury"
          highlight="Inventory"
          description="Each vehicle is hand-selected and meticulously maintained to meet the highest standards of quality, performance, and prestige."
        />

        <AnimateOnScroll animation="fade-up" delay={100}>
          <div className="flex flex-wrap justify-center gap-3 mb-20">
            {VEHICLE_CATEGORIES.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-400 transform hover:scale-105 ${
                  selectedCategory === cat.id
                    ? 'gradient-bg text-black filter-pill-active scale-105'
                    : 'glass-card text-gray-400 hover:text-white hover:border-white/20'
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>
        </AnimateOnScroll>

        {loading && (
          <div className="flex justify-center py-20">
            <div className="w-10 h-10 border-2 border-[#3DED97] border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {error && !loading && (
          <div className="text-center py-20 text-gray-500">
            <p className="text-xl">{error}</p>
          </div>
        )}

        {!loading && !error && vehicles.length === 0 && (
          <div className="text-center py-20 text-gray-500">
            <p className="text-xl">Our inventory is being updated.</p>
            <p className="text-sm mt-2">Please check back soon or contact us directly.</p>
          </div>
        )}

        <div className="space-y-16" key={selectedCategory}>
          {filteredVehicles.map((vehicle, index) => {
            const isReversed = index % 2 !== 0;
            return (
              <AnimateOnScroll
                key={vehicle.id}
                animation={isReversed ? 'fade-left' : 'fade-right'}
                delay={index * 60}
              >
                <div className="group premium-card car-row-card glass-card">
                  <div className={`flex flex-col ${isReversed ? 'lg:flex-row-reverse' : 'lg:flex-row'}`}>
                    <div className="relative lg:w-[52%] h-80 lg:h-auto lg:min-h-[460px] overflow-hidden">
                      <img
                        src={vehicle.imageUrl || PLACEHOLDER_IMAGE}
                        alt={vehicle.name}
                        className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                        onError={(e) => {
                          e.target.src = PLACEHOLDER_IMAGE;
                        }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent lg:bg-gradient-to-r lg:from-transparent lg:via-transparent lg:to-black/50" />

                      <div className="absolute top-5 left-5 flex items-center gap-3">
                        <span className="gradient-bg text-black px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider">
                          {vehicle.category}
                        </span>
                        <span className="glass-effect text-white px-3 py-1.5 rounded-full text-xs font-mono">
                          #{String(index + 1).padStart(2, '0')}
                        </span>
                      </div>
                    </div>

                    <div className="lg:w-[48%] p-8 lg:p-12 flex flex-col justify-center">
                      <h3 className="font-display text-3xl sm:text-4xl font-bold text-white mb-3 group-hover:text-[#3DED97] transition-colors duration-500">
                        {vehicle.name}
                      </h3>
                      <p className="text-gray-400 mb-8 text-base italic">{vehicle.tagline}</p>

                      <div className="mb-8">
                        <h4 className="text-xs font-bold text-[#3DED97] uppercase tracking-[0.2em] mb-4">
                          Key Features
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {(vehicle.features || []).map((feature, fi) => (
                            <span key={fi} className="feature-chip flex items-center gap-1.5">
                              <CheckCircle className="h-3 w-3 text-[#3DED97]" />
                              {feature}
                            </span>
                          ))}
                        </div>
                      </div>

                      <div className="flex flex-col sm:flex-row gap-3">
                        <a href="tel:08066312634" className="btn-primary flex-1 !py-3.5 !text-base !rounded-xl">
                          <Phone className="h-4 w-4" />
                          Inquire Now
                        </a>
                        <a
                          href="#contact"
                          className="flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl font-semibold text-[#3DED97] glass-card hover:border-[#3DED97]/40 transition-all duration-300"
                        >
                          Schedule Viewing
                          <ArrowRight className="h-4 w-4" />
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </AnimateOnScroll>
            );
          })}
        </div>

        {!loading && !error && filteredVehicles.length === 0 && vehicles.length > 0 && (
          <div className="text-center py-20 text-gray-500">
            <p className="text-xl">No vehicles in this category.</p>
          </div>
        )}

        <AnimateOnScroll animation="scale-up" delay={200}>
          <div className="text-center mt-20">
            <a href="tel:08066312634" className="btn-primary">
              Speak With Our Team
              <ArrowRight className="h-5 w-5" />
            </a>
          </div>
        </AnimateOnScroll>
      </div>
    </section>
  );
};

export default Inventory;
